import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  User, Briefcase, DollarSign, LogOut, Save, Phone, Mail, MapPin,
  Download, Calendar as CalendarIcon, AlertTriangle, Check, Crown, X,
  Users, Plus, Trash2, Percent, Image, CreditCard, Copy, FileText, Star, Edit2,
  HelpCircle, ExternalLink, Loader2, Bell
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default template content suggestions for each service type
const DEFAULT_TEMPLATE_CONTENT = {
  "Pool Service": `- Test and balance water chemistry (pH, chlorine, alkalinity)
- Skim surface for debris and leaves
- Brush pool walls and tile line
- Vacuum pool floor
- Clean skimmer and pump baskets
- Backwash filter if needed
- Check water level and adjust
- Inspect pump and equipment
- Add chemicals as needed
- Note any issues or repairs needed`,

  "Lawn Service": `- Mow lawn at proper height
- Edge along sidewalks, driveways, and beds
- Trim around obstacles and fence lines
- Blow clippings from hard surfaces
- Check irrigation system operation
- Remove visible weeds
- Inspect for pest or disease issues
- Clean up all debris
- Note any areas needing attention`,

  "Pest Control": `- Inspect interior for pest activity
- Check exterior perimeter
- Treat baseboards and entry points
- Apply barrier spray outside
- Check and refresh bait stations
- Inspect attic/crawl space access
- Seal visible entry points
- Remove spider webs
- Document findings and treatment
- Provide recommendations`,

  "HVAC": `- Check thermostat operation
- Replace/clean air filter
- Inspect ductwork for leaks
- Clean condenser coils
- Check refrigerant levels
- Test system operation (heat/cool)
- Inspect electrical connections
- Lubricate moving parts
- Check drain line and pan
- Document readings and findings`,

  "Cleaning": `- Dust all surfaces and furniture
- Vacuum carpets and rugs
- Mop hard floors
- Clean and sanitize bathrooms
- Clean and sanitize kitchen
- Wipe down appliances
- Empty trash bins
- Clean mirrors and glass
- Make beds (if requested)
- Note any issues or damage`,

  "General Maintenance": `- Inspect requested areas
- Complete repair work
- Test operation after repair
- Clean work area
- Document work completed
- Note any additional issues found
- Provide recommendations
- Take before/after photos
- Update client on status`
};

const PRICING_TIERS = [
  {
    id: 'free',
    name: 'Starter',
    price: 'Free',
    priceNum: 0,
    features: ['1 user', '5 jobs/month', 'Quote & invoice', 'SMS reminders', 'Venmo/CashApp payments']
  },
  {
    id: 'solo',
    name: 'Solo Pro',
    price: '$19/mo',
    priceNum: 19,
    features: ['1 user', 'Unlimited jobs', 'Custom logo', 'Tax calculations', 'Stripe payments', 'CSV export']
  },
  {
    id: 'crew',
    name: 'Crew',
    price: '$49/mo',
    priceNum: 49,
    features: ['Up to 10 users', 'Unlimited jobs', 'Everything in Solo Pro', 'Team management', 'Shared calendar']
  }
];

export default function Profile() {
  const { user, updateProfile, logout, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(null);
  const [exportEndDate, setExportEndDate] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showImageDownloadModal, setShowImageDownloadModal] = useState(false);
  const [imageDownloadPrice, setImageDownloadPrice] = useState(null);
  const [pendingDownloads, setPendingDownloads] = useState([]);
  const [processingImageDownload, setProcessingImageDownload] = useState(false);
  const [taxRates, setTaxRates] = useState([]);
  const [team, setTeam] = useState({ members: [], pending_invites: [] });
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const logoInputRef = useRef(null);
  
  // Note Templates state
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    template_name: '',
    service_type: '',
    template_content: ''
  });
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    business_name: user?.business_name || '',
    business_phone: user?.business_phone || '',
    business_email: user?.business_email || '',
    business_address_street: user?.business_address_street || '',
    business_address_city: user?.business_address_city || '',
    business_address_state: user?.business_address_state || '',
    business_address_zip: user?.business_address_zip || '',
    payment_method: user?.payment_method || 'venmo',
    payment_username: user?.payment_username || ''
  });

  const [newTaxRate, setNewTaxRate] = useState({
    name: '',
    rate: '',
    applies_to_labor: false,
    applies_to_materials: true
  });

  const [planLimits, setPlanLimits] = useState(null);
  const [usageData, setUsageData] = useState(null);
  
  // Reminder settings state
  const [reminderSettings, setReminderSettings] = useState({
    enabled: false,
    sms_enabled: true,
    email_enabled: true,
    reminder_hours: [24, 1]
  });
  const [reminderLogs, setReminderLogs] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [testingReminder, setTestingReminder] = useState(false);

  // Fetch reminder settings
  const fetchReminderSettings = async () => {
    try {
      const response = await axios.get(`${API}/reminders/settings`);
      setReminderSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch reminder settings:', error);
    }
  };

  // Fetch reminder logs
  const fetchReminderLogs = async () => {
    try {
      const response = await axios.get(`${API}/reminders/logs?limit=20`);
      setReminderLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch reminder logs:', error);
    }
  };

  // Update reminder settings
  const updateReminderSettings = async (updates) => {
    setLoadingReminders(true);
    try {
      const newSettings = { ...reminderSettings, ...updates };
      await axios.put(`${API}/reminders/settings`, newSettings);
      setReminderSettings(newSettings);
      toast.success('Reminder settings updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setLoadingReminders(false);
    }
  };

  // Send test reminder
  const sendTestReminder = async (type = 'both') => {
    setTestingReminder(true);
    try {
      await axios.post(`${API}/reminders/test`, { type });
      toast.success('Test reminder sent! Check your phone/email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test reminder');
    } finally {
      setTestingReminder(false);
    }
  };

  // Fetch usage data
  const fetchUsageData = async () => {
    try {
      const response = await axios.get(`${API}/usage`);
      setUsageData(response.data);
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
    }
  };

  // Handle Stripe checkout success redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const tier = urlParams.get('tier');
    const success = urlParams.get('success');
    const stripeConnected = urlParams.get('stripe_connected');
    const stripeRefresh = urlParams.get('stripe_refresh');
    
    // Handle Stripe Connect callback
    if (stripeConnected === 'true') {
      // Verify the Stripe account status
      const verifyStripeConnection = async () => {
        try {
          const response = await axios.get(`${API}/stripe/connect/status`);
          await fetchUser();
          
          if (response.data.connected) {
            toast.success('Stripe account connected successfully! You can now accept card payments.');
          } else if (response.data.requirements?.length > 0) {
            toast.warning('Stripe account needs more information. Please complete the setup.');
          } else {
            toast.success('Stripe account connected! It may take a moment for full activation.');
          }
        } catch (error) {
          console.error('Failed to verify Stripe status:', error);
          toast.success('Stripe account setup initiated! Verifying connection...');
          await fetchUser();
        }
      };
      verifyStripeConnection();
      setActiveTab('payment');
      window.history.replaceState({}, '', '/profile');
    } else if (stripeConnected === 'false' || stripeRefresh === 'true') {
      toast.error('Stripe setup was not completed. Please try again to finish connecting your account.');
      setActiveTab('payment');
      window.history.replaceState({}, '', '/profile');
    }
    
    if (sessionId && success === 'true') {
      // Poll for subscription status
      const checkStatus = async () => {
        try {
          const response = await axios.get(`${API}/stripe/checkout/status/${sessionId}`);
          
          if (response.data.status === 'success') {
            // Refresh user data to get updated subscription
            await fetchUser();
            
            const trialEnd = response.data.trial_end 
              ? new Date(response.data.trial_end).toLocaleDateString()
              : null;
            
            toast.success(
              trialEnd 
                ? `Welcome to ${tier === 'crew' ? 'Crew' : 'Solo Pro'}! Your 14-day free trial has started.`
                : `Successfully upgraded to ${tier === 'crew' ? 'Crew' : 'Solo Pro'}!`
            );
            
            // Clean up URL
            window.history.replaceState({}, '', '/profile');
          }
        } catch (error) {
          console.error('Failed to verify subscription:', error);
        }
      };
      
      checkStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch plan limits on load
  useEffect(() => {
    const fetchLimits = async () => {
      try {
        const response = await axios.get(`${API}/jobs/limits`);
        setPlanLimits(response.data.plan_limits);
      } catch (error) {
        console.error('Failed to fetch plan limits');
      }
    };
    fetchLimits();
  }, []);

  useEffect(() => {
    fetchTaxRates();
    fetchNoteTemplates();
    fetchImageDownloadInfo();
    fetchUsageData();
    // Fetch team if plan allows more than 1 team member
    const teamFeaturesEnabled = planLimits?.max_team_members > 1;
    if (teamFeaturesEnabled) {
      fetchTeam();
    }
    // Fetch reminder settings if plan has auto reminders
    if (planLimits?.has_auto_reminders) {
      fetchReminderSettings();
      fetchReminderLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planLimits?.max_team_members, planLimits?.has_auto_reminders]);

  const fetchTaxRates = async () => {
    try {
      const response = await axios.get(`${API}/tax-rates`);
      setTaxRates(response.data.tax_rates || []);
    } catch (error) {
      console.error('Failed to fetch tax rates');
    }
  };

  const fetchNoteTemplates = async () => {
    try {
      const response = await axios.get(`${API}/note-templates`);
      setNoteTemplates(response.data.templates || []);
      setServiceTypes(response.data.service_types || []);
    } catch (error) {
      console.error('Failed to fetch note templates');
    }
  };

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeam(response.data);
    } catch (error) {
      console.error('Failed to fetch team');
    }
  };

  const fetchImageDownloadInfo = async () => {
    try {
      const response = await axios.get(`${API}/data-export/image-download/info`);
      setImageDownloadPrice(response.data.price);
      setPendingDownloads(response.data.pending_downloads || []);
    } catch (error) {
      console.error('Failed to fetch image download info');
    }
  };

  const handlePurchaseImageDownload = async () => {
    setProcessingImageDownload(true);
    try {
      const response = await axios.post(`${API}/data-export/image-download/checkout`);
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingImageDownload(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile(formData);
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out');
  };

  const handleAddTaxRate = async () => {
    if (!newTaxRate.name || !newTaxRate.rate) {
      toast.error('Enter tax name and rate');
      return;
    }
    try {
      await axios.post(`${API}/tax-rates`, {
        ...newTaxRate,
        rate: parseFloat(newTaxRate.rate)
      });
      await fetchTaxRates();
      setNewTaxRate({ name: '', rate: '', applies_to_labor: false, applies_to_materials: true });
      toast.success('Tax rate added');
    } catch (error) {
      toast.error('Failed to add tax rate');
    }
  };

  const handleDeleteTaxRate = async (taxId) => {
    try {
      await axios.delete(`${API}/tax-rates/${taxId}`);
      await fetchTaxRates();
      toast.success('Tax rate deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 500000) {
      toast.error('Logo must be under 500KB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.post(`${API}/logo`, { logo: reader.result });
        await fetchUser();
        toast.success('Logo uploaded');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to upload logo');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/logo`);
      await fetchUser();
      toast.success('Logo deleted');
    } catch (error) {
      toast.error('Failed to delete logo');
    }
  };

  const handleInviteTeamMember = async () => {
    if (!inviteEmail || !inviteName) return;
    setInviting(true);
    try {
      const response = await axios.post(`${API}/team/invite`, { 
        email: inviteEmail, 
        name: inviteName,
        role: 'member' 
      });
      await fetchTeam();
      toast.success(`Invite sent to ${inviteName}`);
      setInviteEmail('');
      setInviteName('');
      setShowInviteModal(false);
      
      // Copy invite link
      const inviteUrl = `${window.location.origin}${response.data.invite_url}`;
      navigator.clipboard.writeText(inviteUrl);
      toast.success('Invite link copied to clipboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveTeamMember = async (memberId) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await axios.delete(`${API}/team/member/${memberId}`);
      await fetchTeam();
      toast.success('Member removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleStripeCheckout = async (tier) => {
    // Create Stripe checkout session
    try {
      const response = await axios.post(`${API}/stripe/checkout/subscription`, {
        tier: tier,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url || response.data.url;
    } catch (error) {
      console.error('Stripe checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    }
  };

  const handleManageSubscription = async () => {
    // Open Stripe Customer Portal
    try {
      const response = await axios.get(`${API}/stripe/subscription/portal`);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
    }
  };

  const handleConnectStripe = async () => {
    setStripeConnecting(true);
    try {
      // Pass the current page URL so Stripe can redirect back
      const response = await axios.post(`${API}/stripe/connect/setup`, {
        origin_url: window.location.origin
      });
      
      if (response.data.onboarding_url) {
        // Redirect to Stripe Connect onboarding
        window.location.href = response.data.onboarding_url;
      } else {
        toast.error('Failed to start Stripe onboarding');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Stripe');
      setStripeConnecting(false);
    }
  };

  const handleDisconnectStripe = async () => {
    if (!window.confirm('Disconnect Stripe?')) return;
    try {
      await axios.delete(`${API}/stripe/connect`);
      await fetchUser();
      toast.success('Stripe disconnected');
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleCancelSubscription = async () => {
    try {
      // Use Stripe subscription cancel if user has a Stripe subscription
      if (user?.stripe_subscription_id) {
        await axios.post(`${API}/stripe/subscription/cancel`);
        await fetchUser();
        setShowCancelModal(false);
        toast.success('Your subscription will be cancelled at the end of your billing period');
      } else {
        // Fallback for non-Stripe subscriptions
        await axios.post(`${API}/auth/cancel-subscription`);
        await fetchUser();
        setShowCancelModal(false);
        toast.success('Subscription cancelled');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportStartDate) params.append('start_date', format(exportStartDate, 'yyyy-MM-dd'));
      if (exportEndDate) params.append('end_date', format(exportEndDate, 'yyyy-MM-dd'));
      
      const response = await axios.get(`${API}/jobs/export/csv?${params.toString()}`);
      
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.total_jobs} jobs`);
      setShowExportModal(false);
    } catch (error) {
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const formatEndDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  // Note Template handlers
  const handleCreateTemplate = async () => {
    if (!templateForm.template_name || !templateForm.service_type || !templateForm.template_content) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      if (editingTemplate) {
        await axios.put(`${API}/note-templates/${editingTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/note-templates`, templateForm);
        toast.success('Template created');
      }
      await fetchNoteTemplates();
      setShowTemplateModal(false);
      setEditingTemplate(null);
      setTemplateForm({ template_name: '', service_type: '', template_content: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleServiceTypeChange = (serviceType) => {
    // When creating new template, always update content when service type changes
    // (unless user has manually edited the content to something different from any suggestion)
    const currentContentIsSuggestion = Object.values(DEFAULT_TEMPLATE_CONTENT).includes(templateForm.template_content.trim());
    const shouldAutoFill = !editingTemplate && (
      !templateForm.template_content.trim() || currentContentIsSuggestion
    );
    const suggestedContent = DEFAULT_TEMPLATE_CONTENT[serviceType] || '';
    
    setTemplateForm(prev => ({
      ...prev,
      service_type: serviceType,
      // Auto-fill content with suggestion if applicable
      template_content: shouldAutoFill ? suggestedContent : prev.template_content,
      // Also suggest a template name if empty or was auto-generated
      template_name: !editingTemplate && (!prev.template_name || serviceTypes.some(t => prev.template_name === `${t} Checklist`))
        ? (serviceType && serviceType !== 'Custom' ? `${serviceType} Checklist` : '')
        : prev.template_name
    }));
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      template_name: template.template_name,
      service_type: template.service_type,
      template_content: template.template_content
    });
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/note-templates/${templateId}`);
      await fetchNoteTemplates();
      toast.success('Template deleted');
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleSetActiveTemplate = async (templateId) => {
    try {
      await axios.post(`${API}/note-templates/${templateId}/set-active`);
      await fetchNoteTemplates();
      toast.success('Default template updated');
    } catch (error) {
      toast.error('Failed to set default template');
    }
  };

  const handleOpenNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ template_name: '', service_type: '', template_content: '' });
    setShowTemplateModal(true);
  };

  const currentTier = PRICING_TIERS.find(t => t.id === user?.subscription_tier) || PRICING_TIERS[0];
  const isPaid = user?.subscription_tier !== 'free';
  // Show team features if plan allows more than 1 team member
  const hasTeamFeatures = planLimits?.max_team_members > 1;

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'usage', label: 'Usage', icon: Briefcase },
    { id: 'payment', label: 'Payment', icon: DollarSign },
    { id: 'tax', label: 'Tax Rates', icon: Percent },
    { id: 'templates', label: 'Templates', icon: FileText },
    ...(planLimits?.has_auto_reminders ? [{ id: 'reminders', label: 'Reminders', icon: Bell }] : []),
    ...(hasTeamFeatures ? [{ id: 'team', label: 'Team', icon: Users }] : []),
    { id: 'subscription', label: 'Plan', icon: Crown }
  ];

  return (
    <div className="p-4 animate-fade-in pb-24" data-testid="profile-page">
      {/* Header */}
      <div className="mb-4">
        <h1 className="font-heading text-3xl font-bold text-slate-dark">Settings</h1>
      </div>

      {/* Quick Links */}
      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl border border-emerald-200 p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-bold text-slate-800">Track Business Expenses</p>
            <p className="text-sm text-slate-600">For tax time & profitability</p>
          </div>
          <Button 
            onClick={() => window.location.href = '/expenses'}
            className="bg-[#FF5F00]"
            size="sm"
          >
            <DollarSign className="w-4 h-4 mr-1" />
            Expenses
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-[#FF5F00] text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Logo (Solo/Crew only) */}
          {isPaid && (
            <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4">
              <h2 className="font-heading text-lg font-bold text-slate-dark mb-3 flex items-center gap-2">
                <Image className="w-5 h-5" />
                Business Logo
              </h2>
              
              <div className="flex items-center gap-4">
                {user?.logo ? (
                  <img src={user.logo} alt="Logo" className="w-20 h-20 rounded-lg object-contain bg-slate-100" />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Image className="w-8 h-8 text-slate-300" />
                  </div>
                )}
                
                <div className="flex-1 space-y-2">
                  <input
                    type="file"
                    ref={logoInputRef}
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    {user?.logo ? 'Change Logo' : 'Upload Logo'}
                  </Button>
                  {user?.logo && (
                    <Button
                      type="button"
                      onClick={handleDeleteLogo}
                      variant="outline"
                      size="sm"
                      className="w-full text-red-600 border-red-200"
                    >
                      Remove
                    </Button>
                  )}
                  <p className="text-xs text-slate-500">Max 500KB, PNG/JPG</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4 space-y-4">
            <h2 className="font-heading text-lg font-bold text-slate-dark flex items-center gap-2">
              <User className="w-5 h-5" />
              Business Info
            </h2>
            
            <div className="space-y-2">
              <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">Your Name</Label>
              <Input name="name" value={formData.name} onChange={handleChange} className="h-12 border-2 border-slate-300" />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">Business Name</Label>
              <div className="relative">
                <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input name="business_name" value={formData.business_name} onChange={handleChange} placeholder="Your Business LLC" className="h-12 pl-12 border-2 border-slate-300" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input name="business_phone" value={formData.business_phone} onChange={handleChange} className="h-12 pl-10 border-2 border-slate-300" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input name="business_email" type="email" value={formData.business_email} onChange={handleChange} className="h-12 pl-10 border-2 border-slate-300" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Address
              </Label>
              <Input name="business_address_street" value={formData.business_address_street} onChange={handleChange} placeholder="Street" className="h-12 border-2 border-slate-300" />
              <div className="grid grid-cols-6 gap-2">
                <Input name="business_address_city" value={formData.business_address_city} onChange={handleChange} placeholder="City" className="col-span-3 h-12 border-2 border-slate-300" />
                <Input name="business_address_state" value={formData.business_address_state} onChange={handleChange} placeholder="ST" className="col-span-1 h-12 border-2 border-slate-300" />
                <Input name="business_address_zip" value={formData.business_address_zip} onChange={handleChange} placeholder="ZIP" className="col-span-2 h-12 border-2 border-slate-300" />
              </div>
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full h-14 bg-[#FF5F00] hover:bg-orange-600 text-white font-bold uppercase text-lg">
            <Save className="w-5 h-5 mr-2" />
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>

          <Button onClick={handleLogout} variant="outline" className="w-full h-12 text-slate-600 border-2 mt-4">
            <LogOut className="w-4 h-4 mr-2" /> Log Out
          </Button>
        </form>
      )}

      {/* Usage Tab */}
      {activeTab === 'usage' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4">
            <h2 className="font-heading text-lg font-bold text-slate-dark mb-4">Monthly Usage</h2>
            <p className="text-slate-500 text-sm mb-4">
              {usageData?.month ? `Usage for ${new Date(usageData.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}` : 'Loading...'}
            </p>
            
            {usageData ? (
              <div className="space-y-4">
                {/* Jobs */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-slate-700">Jobs Created</span>
                    <span className="text-sm text-slate-600">
                      {usageData.usage.jobs_created}
                      {usageData.limits.max_jobs_per_month ? ` / ${usageData.limits.max_jobs_per_month}` : ' (Unlimited)'}
                    </span>
                  </div>
                  {usageData.limits.max_jobs_per_month && (
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${usageData.usage.jobs_created >= usageData.limits.max_jobs_per_month ? 'bg-red-500' : 'bg-[#FF5F00]'}`}
                        style={{ width: `${Math.min(100, (usageData.usage.jobs_created / usageData.limits.max_jobs_per_month) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* Emails */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-slate-700">Emails Sent</span>
                    <span className="text-sm text-slate-600">
                      {usageData.usage.emails_sent}
                      {usageData.limits.max_emails_per_month ? ` / ${usageData.limits.max_emails_per_month}` : ' (Unlimited)'}
                    </span>
                  </div>
                  {usageData.limits.max_emails_per_month && (
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${usageData.usage.emails_sent >= usageData.limits.max_emails_per_month ? 'bg-red-500' : 'bg-blue-500'}`}
                        style={{ width: `${Math.min(100, (usageData.usage.emails_sent / usageData.limits.max_emails_per_month) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* SMS */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-slate-700">SMS Sent</span>
                    <span className="text-sm text-slate-600">
                      {usageData.usage.sms_sent}
                      {usageData.limits.max_sms_per_month ? ` / ${usageData.limits.max_sms_per_month}` : ' (Unlimited)'}
                    </span>
                  </div>
                  {usageData.limits.max_sms_per_month && (
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${usageData.usage.sms_sent >= usageData.limits.max_sms_per_month ? 'bg-red-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(100, (usageData.usage.sms_sent / usageData.limits.max_sms_per_month) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* Expenses */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-slate-700">Expenses Logged</span>
                    <span className="text-sm text-slate-600">
                      {usageData.usage.expenses_created}
                      {usageData.limits.max_expenses_per_month ? ` / ${usageData.limits.max_expenses_per_month}` : ' (Unlimited)'}
                    </span>
                  </div>
                  {usageData.limits.max_expenses_per_month && (
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${usageData.usage.expenses_created >= usageData.limits.max_expenses_per_month ? 'bg-red-500' : 'bg-purple-500'}`}
                        style={{ width: `${Math.min(100, (usageData.usage.expenses_created / usageData.limits.max_expenses_per_month) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* Team Members */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-slate-700">Team Members</span>
                    <span className="text-sm text-slate-600">
                      {usageData.usage.team_members}
                      {usageData.limits.max_team_members ? ` / ${usageData.limits.max_team_members}` : ' (Unlimited)'}
                    </span>
                  </div>
                  {usageData.limits.max_team_members && (
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${usageData.usage.team_members >= usageData.limits.max_team_members ? 'bg-red-500' : 'bg-amber-500'}`}
                        style={{ width: `${Math.min(100, (usageData.usage.team_members / usageData.limits.max_team_members) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">Loading usage data...</p>
            )}
          </div>

          <p className="text-xs text-slate-400 text-center">
            Usage resets on the 1st of each month
          </p>
        </div>
      )}

      {/* Payment Tab */}
      {activeTab === 'payment' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4">
            <h2 className="font-heading text-lg font-bold text-slate-dark mb-4">Customer Payments</h2>
            <p className="text-slate-500 text-sm mb-4">Choose how your customers pay you</p>
            
            <div className="grid grid-cols-3 gap-2 mb-4">
              {['venmo', 'cashapp', ...(planLimits?.has_stripe ? ['stripe'] : [])].map(method => (
                <button
                  key={method}
                  onClick={() => setFormData(prev => ({ ...prev, payment_method: method }))}
                  className={`p-3 rounded-xl border-2 transition-colors ${
                    formData.payment_method === method ? 'border-[#FF5F00] bg-orange-50' : 'border-slate-200'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-1 mx-auto ${
                    method === 'venmo' ? 'bg-blue-500' : method === 'cashapp' ? 'bg-emerald-500' : 'bg-purple-600'
                  }`}>
                    {method === 'stripe' ? <CreditCard className="w-4 h-4 text-white" /> : <DollarSign className="w-4 h-4 text-white" />}
                  </div>
                  <span className="text-xs font-bold capitalize">{method}</span>
                </button>
              ))}
            </div>

            {formData.payment_method === 'stripe' ? (
              <div className="space-y-3">
                {user?.stripe_account_connected ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <p className="text-emerald-700 font-medium flex items-center gap-2">
                      <Check className="w-4 h-4" /> Stripe Connected
                    </p>
                    <p className="text-emerald-600 text-sm mb-3">Account: {user.stripe_account_id}</p>
                    <div className="flex gap-2">
                      <Button onClick={handleDisconnectStripe} variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
                        Disconnect
                      </Button>
                      <a 
                        href="https://dashboard.stripe.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-700 text-sm font-medium"
                      >
                        Stripe Dashboard <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <h4 className="font-bold text-purple-800 mb-1">Accept Card Payments</h4>
                      <p className="text-purple-700 text-sm mb-3">
                        Connect your Stripe account to let customers pay with credit/debit cards. Funds deposit directly to your bank.
                      </p>
                      <Button 
                        onClick={handleConnectStripe} 
                        disabled={stripeConnecting}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        {stripeConnecting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" /> Connect Stripe Account
                          </>
                        )}
                      </Button>
                    </div>
                    <button 
                      onClick={() => navigate('/help/stripe')}
                      className="flex items-center gap-2 text-slate-600 hover:text-purple-600 text-sm w-full justify-center"
                    >
                      <HelpCircle className="w-4 h-4" />
                      Need help setting up Stripe?
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">
                  {formData.payment_method === 'venmo' ? 'Venmo Username' : 'Cash App $Cashtag'}
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">
                    {formData.payment_method === 'cashapp' ? '$' : '@'}
                  </span>
                  <Input
                    name="payment_username"
                    value={formData.payment_username}
                    onChange={handleChange}
                    className="h-12 pl-10 border-2 border-slate-300"
                  />
                </div>
                <Button onClick={handleSubmit} disabled={loading} className="w-full h-12 bg-[#FF5F00]">
                  <Save className="w-4 h-4 mr-2" /> Save Payment Method
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tax Rates Tab */}
      {activeTab === 'tax' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4">
            <h2 className="font-heading text-lg font-bold text-slate-dark mb-2">Tax Rates</h2>
            <p className="text-slate-500 text-sm mb-4">Add tax rates that apply to your jobs</p>
            
            {/* Existing Tax Rates */}
            {taxRates.length > 0 && (
              <div className="space-y-2 mb-4">
                {taxRates.map(tax => (
                  <div key={tax.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">{tax.name}</p>
                      <p className="text-sm text-slate-500">
                        {tax.rate}% • 
                        {tax.applies_to_labor && ' Labor'}
                        {tax.applies_to_labor && tax.applies_to_materials && ' &'}
                        {tax.applies_to_materials && ' Materials'}
                      </p>
                    </div>
                    <button onClick={() => handleDeleteTaxRate(tax.id)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add New Tax Rate */}
            <div className="border-t pt-4 space-y-3">
              <h3 className="font-bold text-slate-700">Add Tax Rate</h3>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Tax Name (e.g., Comal County)"
                  value={newTaxRate.name}
                  onChange={(e) => setNewTaxRate(prev => ({ ...prev, name: e.target.value }))}
                  className="h-10 border-2 border-slate-300"
                />
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Rate"
                    value={newTaxRate.rate}
                    onChange={(e) => setNewTaxRate(prev => ({ ...prev, rate: e.target.value }))}
                    className="h-10 pr-8 border-2 border-slate-300"
                  />
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={newTaxRate.applies_to_labor}
                    onCheckedChange={(checked) => setNewTaxRate(prev => ({ ...prev, applies_to_labor: checked }))}
                  />
                  <span className="text-sm text-slate-700">Tax Labor</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={newTaxRate.applies_to_materials}
                    onCheckedChange={(checked) => setNewTaxRate(prev => ({ ...prev, applies_to_materials: checked }))}
                  />
                  <span className="text-sm text-slate-700">Tax Materials</span>
                </label>
              </div>
              
              <Button onClick={handleAddTaxRate} className="w-full h-10 bg-[#FF5F00]">
                <Plus className="w-4 h-4 mr-2" /> Add Tax Rate
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading text-lg font-bold text-slate-dark">Note Templates</h2>
            </div>
            <p className="text-slate-500 text-sm mb-4">
              Create templates for different service types. The active template will auto-fill job notes.
            </p>
            
            {/* Existing Templates */}
            {noteTemplates.length > 0 ? (
              <div className="space-y-3 mb-4">
                {noteTemplates.map(template => (
                  <div 
                    key={template.id} 
                    className={`p-4 rounded-lg border-2 ${
                      template.is_active 
                        ? 'border-[#FF5F00] bg-orange-50' 
                        : 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-slate-900">{template.template_name}</p>
                          {template.is_active && (
                            <span className="bg-[#FF5F00] text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Star className="w-3 h-3" /> DEFAULT
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{template.service_type}</p>
                      </div>
                      <div className="flex gap-1">
                        <button 
                          onClick={() => handleEditTemplate(template)} 
                          className="p-2 text-slate-500 hover:bg-slate-100 rounded"
                          data-testid={`edit-template-${template.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteTemplate(template.id)} 
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                          data-testid={`delete-template-${template.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded p-3 text-sm text-slate-600 whitespace-pre-wrap max-h-32 overflow-y-auto border border-slate-200">
                      {template.template_content}
                    </div>
                    
                    {!template.is_active && (
                      <Button 
                        onClick={() => handleSetActiveTemplate(template.id)} 
                        variant="outline" 
                        size="sm" 
                        className="mt-3 w-full"
                        data-testid={`set-active-${template.id}`}
                      >
                        <Star className="w-4 h-4 mr-2" /> Set as Default
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400 mb-4">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No templates yet. Create your first template!</p>
              </div>
            )}
            
            <Button 
              onClick={handleOpenNewTemplate} 
              className="w-full h-12 bg-[#FF5F00]"
              data-testid="create-template-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Template
            </Button>
          </div>
        </div>
      )}

      {/* Reminders Tab (plans with has_auto_reminders enabled) */}
      {activeTab === 'reminders' && planLimits?.has_auto_reminders && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-lg font-bold text-slate-dark">Automatic Reminders</h2>
                <p className="text-sm text-slate-500">Send appointment reminders to your customers automatically</p>
              </div>
              <Bell className="w-6 h-6 text-[#FF5F00]" />
            </div>

            {!reminderSettings.enabled ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                <p className="text-amber-700">{reminderSettings.reason || 'Auto reminders are not enabled on your plan.'}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* SMS Reminders Toggle */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800">SMS Reminders</p>
                      <p className="text-sm text-slate-500">Text message reminders via Twilio</p>
                    </div>
                  </div>
                  <Switch
                    checked={reminderSettings.sms_enabled}
                    onCheckedChange={(checked) => updateReminderSettings({ sms_enabled: checked })}
                    disabled={loadingReminders}
                  />
                </div>

                {/* Email Reminders Toggle */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800">Email Reminders</p>
                      <p className="text-sm text-slate-500">Email reminders with appointment details</p>
                    </div>
                  </div>
                  <Switch
                    checked={reminderSettings.email_enabled}
                    onCheckedChange={(checked) => updateReminderSettings({ email_enabled: checked })}
                    disabled={loadingReminders}
                  />
                </div>

                {/* Reminder Timing */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-bold text-slate-800 mb-2">Reminder Timing</p>
                  <p className="text-sm text-slate-500 mb-3">When to send reminders before appointments</p>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 4, 12, 24, 48].map(hours => (
                      <button
                        key={hours}
                        onClick={() => {
                          const current = reminderSettings.reminder_hours || [];
                          const newHours = current.includes(hours)
                            ? current.filter(h => h !== hours)
                            : [...current, hours].sort((a, b) => b - a);
                          if (newHours.length > 0) {
                            updateReminderSettings({ reminder_hours: newHours });
                          }
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          (reminderSettings.reminder_hours || []).includes(hours)
                            ? 'bg-[#FF5F00] text-white'
                            : 'bg-white border border-slate-300 text-slate-600 hover:border-[#FF5F00]'
                        }`}
                      >
                        {hours}h before
                      </button>
                    ))}
                  </div>
                </div>

                {/* Test Reminder */}
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="font-bold text-purple-800 mb-2">Test Your Reminders</p>
                  <p className="text-sm text-purple-600 mb-3">Send a test reminder to yourself to see what your customers will receive.</p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => sendTestReminder('sms')}
                      disabled={testingReminder || !reminderSettings.sms_enabled}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      {testingReminder ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Phone className="w-4 h-4 mr-1" />}
                      Test SMS
                    </Button>
                    <Button
                      onClick={() => sendTestReminder('email')}
                      disabled={testingReminder || !reminderSettings.email_enabled}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      {testingReminder ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mail className="w-4 h-4 mr-1" />}
                      Test Email
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Recent Reminders Log */}
          {reminderSettings.enabled && reminderLogs.length > 0 && (
            <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4">
              <h3 className="font-heading text-lg font-bold text-slate-dark mb-3">Recent Reminders</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {reminderLogs.map((log, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                    <div className="flex items-center gap-2">
                      {log.type === 'sms' ? (
                        <Phone className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Mail className="w-4 h-4 text-blue-600" />
                      )}
                      <span className="text-slate-600">
                        {log.type === 'sms' ? log.phone : log.email}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 text-xs">
                        {log.reminder_hours}h reminder
                      </span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        log.status === 'sent' || log.status === 'delivered' || log.status === 'queued'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {log.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Team Tab (Team feature enabled plans) */}
      {activeTab === 'team' && hasTeamFeatures && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold text-slate-dark">Team Members</h2>
              <span className="text-sm text-slate-500">{team.members?.length || 1}/{team.max_members || planLimits?.max_team_members || 10}</span>
            </div>
            
            {/* Team Members List */}
            <div className="space-y-2 mb-4">
              {team.members?.map(member => (
                <div key={member.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">{member.name}</p>
                    <p className="text-sm text-slate-500">{member.email} • {member.team_role}</p>
                  </div>
                  {member.team_role !== 'owner' && (
                    <button onClick={() => handleRemoveTeamMember(member.id)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Pending Invites */}
            {team.pending_invites?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-bold text-slate-600 mb-2">Pending Invites</h3>
                {team.pending_invites.map(invite => (
                  <div key={invite.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg mb-2">
                    <div>
                      <p className="font-medium text-amber-800">{invite.name || 'Unnamed'}</p>
                      <p className="text-sm text-amber-600">{invite.email}</p>
                    </div>
                    <span className="text-xs font-bold text-amber-500 bg-amber-100 px-2 py-1 rounded-full">Pending</span>
                  </div>
                ))}
              </div>
            )}
            
            <Button onClick={() => setShowInviteModal(true)} className="w-full h-12 bg-[#FF5F00]">
              <Plus className="w-4 h-4 mr-2" /> Invite Team Member
            </Button>
          </div>
        </div>
      )}

      {/* Subscription Tab */}
      {activeTab === 'subscription' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-lg font-bold text-slate-dark">{currentTier.name}</h2>
                <p className="text-[#FF5F00] font-bold">{currentTier.price}</p>
              </div>
              {user?.cancelled ? (
                <span className="bg-red-100 text-red-700 text-xs font-bold px-3 py-1 rounded-full">CANCELLING</span>
              ) : user?.subscription_status === 'trialing' ? (
                <span className="bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full">TRIAL</span>
              ) : (
                <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-3 py-1 rounded-full">ACTIVE</span>
              )}
            </div>

            {/* Trial info */}
            {user?.trial_end_date && user?.subscription_status === 'trialing' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-blue-800 text-sm font-medium">
                  🎉 You're on a free trial! Your trial ends on {new Date(user.trial_end_date).toLocaleDateString()}.
                </p>
              </div>
            )}

            {user?.subscription_end_date && (
              <p className="text-slate-500 text-sm mb-4">
                {user?.cancelled ? 'Access until' : 'Renews'}: {formatEndDate(user.subscription_end_date)}
              </p>
            )}

            {user?.subscription_tier === 'free' ? (
              <Button onClick={() => setShowUpgradeModal(true)} className="w-full h-12 bg-[#FF5F00] mb-2">
                <Crown className="w-4 h-4 mr-2" /> Upgrade Plan
              </Button>
            ) : (
              <div className="space-y-2">
                {user?.stripe_customer_id && (
                  <Button onClick={handleManageSubscription} className="w-full h-12 bg-slate-800 hover:bg-slate-700">
                    <CreditCard className="w-4 h-4 mr-2" /> Manage Billing
                  </Button>
                )}
                <Button onClick={() => setShowUpgradeModal(true)} variant="outline" className="w-full h-12 border-2">
                  <Crown className="w-4 h-4 mr-2" /> Change Plan
                </Button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4">
            <h2 className="font-heading text-lg font-bold text-slate-dark mb-3 flex items-center gap-2">
              <Download className="w-5 h-5" /> Export Data
            </h2>
            <p className="text-slate-500 text-sm mb-3">
              Your data belongs to you. Download anytime.
            </p>
            <div className="space-y-3">
              <Button onClick={() => setShowExportModal(true)} variant="outline" className="w-full h-12 border-2">
                <FileText className="w-4 h-4 mr-2" /> Download Jobs CSV
                <span className="ml-auto text-xs text-emerald-600 font-medium">FREE</span>
              </Button>
              
              {/* Image Download - Only show if plan has feature */}
              {planLimits?.has_image_download && (
                <div className="border-t border-slate-200 pt-3">
                  <Button 
                    onClick={() => setShowImageDownloadModal(true)} 
                    variant="outline" 
                    className="w-full h-12 border-2 border-purple-200 hover:bg-purple-50"
                  >
                    <Image className="w-4 h-4 mr-2 text-purple-600" /> 
                    <span className="text-purple-700">Download All Images</span>
                    <span className="ml-auto text-xs text-purple-600 font-bold">
                      ${imageDownloadPrice?.toFixed(2) || '4.99'}
                    </span>
                  </Button>
                  <p className="text-slate-400 text-xs mt-2">
                    One-time fee to download all job photos as a ZIP file
                  </p>
                </div>
              )}
              
              {/* Pending Downloads */}
              {pendingDownloads.length > 0 && (
                <div className="border-t border-slate-200 pt-3">
                  <p className="text-sm font-medium text-slate-700 mb-2">Your Downloads</p>
                  {pendingDownloads.map((download) => (
                    <div key={download.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div>
                        <p className="text-sm font-medium text-emerald-800">
                          {download.image_count} images ({download.file_size_mb}MB)
                        </p>
                        <p className="text-xs text-emerald-600">
                          Expires: {new Date(download.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                      <a 
                        href={download.download_url} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-emerald-700"
                      >
                        Download
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {!user?.cancelled && user?.subscription_tier !== 'free' && (
            <Button onClick={() => setShowCancelModal(true)} variant="outline" className="w-full h-10 text-red-600 border-red-200 hover:bg-red-50">
              <AlertTriangle className="w-4 h-4 mr-2" /> Cancel Subscription
            </Button>
          )}
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 text-center mb-2">Cancel Subscription?</h2>
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <p className="text-slate-700"><strong>Access until:</strong></p>
              <p className="text-2xl font-heading font-bold text-slate-900">{formatEndDate(user?.subscription_end_date)}</p>
              <p className="text-red-600 text-sm font-medium mt-2">After this, all data will be deleted.</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setShowCancelModal(false)} variant="outline" className="flex-1 h-12">Keep</Button>
              <Button onClick={handleCancelSubscription} className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-8 animate-slide-up">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold text-slate-900">Choose Plan</h2>
              <button onClick={() => setShowUpgradeModal(false)} className="p-2"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="space-y-4">
              {PRICING_TIERS.map((tier) => (
                <div key={tier.id} className={`p-4 rounded-xl border-2 ${user?.subscription_tier === tier.id ? 'border-[#FF5F00] bg-orange-50' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-heading text-lg font-bold text-slate-900">{tier.name}</h3>
                      <p className="text-[#FF5F00] font-bold">{tier.price}</p>
                    </div>
                    {user?.subscription_tier === tier.id ? (
                      <span className="bg-[#FF5F00] text-white text-xs font-bold px-3 py-1 rounded-full">CURRENT</span>
                    ) : tier.priceNum > 0 ? (
                      <Button 
                        onClick={() => handleStripeCheckout(tier.id)} 
                        size="sm" 
                        className="bg-slate-dark"
                        data-testid={`upgrade-to-${tier.id}`}
                      >
                        Upgrade
                      </Button>
                    ) : (
                      <span className="text-slate-400 text-sm">Free</span>
                    )}
                  </div>
                  <ul className="space-y-1">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-emerald-500" />{feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-900">Export Jobs</h2>
              <button onClick={() => setShowExportModal(false)} className="p-2"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <p className="text-slate-500 text-sm mb-4">Select date range (optional)</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="text-xs font-bold mb-1 block">Start</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="w-full h-10 px-3 bg-white border-2 border-slate-300 rounded-lg flex items-center gap-2 text-sm">
                      <CalendarIcon className="w-4 h-4 text-slate-400" />
                      {exportStartDate ? format(exportStartDate, 'MMM d') : 'Any'}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={exportStartDate} onSelect={setExportStartDate} /></PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-xs font-bold mb-1 block">End</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="w-full h-10 px-3 bg-white border-2 border-slate-300 rounded-lg flex items-center gap-2 text-sm">
                      <CalendarIcon className="w-4 h-4 text-slate-400" />
                      {exportEndDate ? format(exportEndDate, 'MMM d') : 'Any'}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={exportEndDate} onSelect={setExportEndDate} /></PopoverContent>
                </Popover>
              </div>
            </div>
            <Button onClick={handleExportCSV} disabled={exporting} className="w-full h-12 bg-[#FF5F00]">
              <Download className="w-4 h-4 mr-2" />{exporting ? 'Exporting...' : 'Download CSV'}
            </Button>
          </div>
        </div>
      )}

      {/* Image Download Modal */}
      {showImageDownloadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-900">Download All Images</h2>
              <button onClick={() => setShowImageDownloadModal(false)} className="p-2"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            
            <div className="bg-purple-50 rounded-xl p-4 mb-4 border border-purple-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Image className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-900">Your Job Photos</p>
                  <p className="text-sm text-slate-600">All images from your jobs in one ZIP file</p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-purple-100">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">One-time download fee</span>
                  <span className="font-bold text-purple-700">${imageDownloadPrice?.toFixed(2) || '4.99'}</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 text-sm text-slate-600 mb-4">
              <div className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Download link valid for 30 days</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Organized by job with original filenames</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Secure payment via Stripe</span>
              </div>
            </div>
            
            <p className="text-xs text-slate-400 mb-4 text-center">
              We charge a small fee to cover bandwidth costs. Your data is always yours.
            </p>
            
            <Button 
              onClick={handlePurchaseImageDownload} 
              disabled={processingImageDownload} 
              className="w-full h-12 bg-purple-600 hover:bg-purple-700"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {processingImageDownload ? 'Processing...' : `Pay $${imageDownloadPrice?.toFixed(2) || '4.99'} & Download`}
            </Button>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-900">Invite Team Member</h2>
              <button onClick={() => { setShowInviteModal(false); setInviteEmail(''); setInviteName(''); }} className="p-2"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <p className="text-slate-500 text-sm mb-4">They'll receive an invite link to join your team.</p>
            <div className="space-y-4">
              <div>
                <label className="text-slate-700 uppercase text-xs tracking-wider font-bold mb-1 block">Name</label>
                <Input
                  type="text"
                  placeholder="John Smith"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  className="h-12 border-2 border-slate-300"
                  data-testid="invite-name-input"
                />
              </div>
              <div>
                <label className="text-slate-700 uppercase text-xs tracking-wider font-bold mb-1 block">Email</label>
                <Input
                  type="email"
                  placeholder="team@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="h-12 border-2 border-slate-300"
                  data-testid="invite-email-input"
                />
              </div>
            </div>
            <Button 
              onClick={handleInviteTeamMember} 
              disabled={inviting || !inviteEmail || !inviteName} 
              className="w-full h-12 bg-[#FF5F00] mt-4"
              data-testid="send-invite-btn"
            >
              {inviting ? 'Sending...' : 'Send Invite'}
            </Button>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-8 animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-900">
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </h2>
              <button 
                onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); }} 
                className="p-2"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">Template Name</Label>
                <Input
                  value={templateForm.template_name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, template_name: e.target.value }))}
                  placeholder="e.g., Weekly Pool Service Checklist"
                  className="h-12 border-2 border-slate-300"
                  data-testid="template-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">Service Type</Label>
                <select
                  value={templateForm.service_type}
                  onChange={(e) => handleServiceTypeChange(e.target.value)}
                  className="w-full h-12 px-3 border-2 border-slate-300 rounded-lg bg-white text-slate-900"
                  data-testid="template-service-type-select"
                >
                  <option value="">Select service type...</option>
                  {serviceTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">Template Content</Label>
                  {!editingTemplate && templateForm.service_type && DEFAULT_TEMPLATE_CONTENT[templateForm.service_type] && (
                    <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                      Auto-filled from {templateForm.service_type}
                    </span>
                  )}
                </div>
                <Textarea
                  value={templateForm.template_content}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, template_content: e.target.value }))}
                  placeholder="Select a service type above to get suggested checklist items, or enter your own..."
                  className="min-h-[200px] border-2 border-slate-300"
                  data-testid="template-content-input"
                />
                <p className="text-xs text-slate-500">
                  {!editingTemplate 
                    ? "Select a service type to auto-fill suggested items. Customize as needed."
                    : "This content will auto-fill the job notes when editing a job."}
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button 
                onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); }}
                variant="outline" 
                className="flex-1 h-12"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateTemplate}
                className="flex-1 h-12 bg-[#FF5F00]"
                data-testid="save-template-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingTemplate ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
